import React from "react";
import { useQuery } from "@tanstack/react-query";
import Card from "components/card";
import {
  MdPeople,
  MdVerified,
  MdContactMail,
  MdQuestionAnswer,
} from "react-icons/md";
import { getDashboardStats } from "api/adapters/admin/admin";

function StatCard({ title, value, icon, bg, loading }) {
  return (
    <Card extra="p-5 hover:shadow-lg hover:shadow-orange-100/50 transition-shadow duration-200 dark:hover:shadow-none">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-black text-navy-700 dark:text-white">
            {loading ? (
              <span className="inline-block h-8 w-20 animate-pulse rounded-lg bg-orange-100 dark:bg-navy-700" />
            ) : (
              value?.toLocaleString() ?? "—"
            )}
          </p>
        </div>
        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg ${bg}`}>
          {icon}
        </div>
      </div>
      <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
        <div className={`h-1 w-2/3 rounded-full ${bg} opacity-40`} />
      </div>
    </Card>
  );
}

const Dashboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: getDashboardStats,
  });

  const stats = data?.data;

  return (
    <div className="flex flex-col gap-5">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Users"
          value={stats?.nTotalUsers}
          loading={isLoading}
          icon={<MdPeople className="h-6 w-6 text-white" />}
          bg="bg-orange-500"
        />
        <StatCard
          title="Active TRPs"
          value={stats?.nActiveTRPs}
          loading={isLoading}
          icon={<MdVerified className="h-6 w-6 text-white" />}
          bg="bg-orange-400"
        />
        <StatCard
          title="Contact Inquiries"
          value={stats?.nTotalInquiries}
          loading={isLoading}
          icon={<MdContactMail className="h-6 w-6 text-white" />}
          bg="bg-orange-300"
        />
        <StatCard
          title="FAQs"
          value={stats?.nTotalFaqs}
          loading={isLoading}
          icon={<MdQuestionAnswer className="h-6 w-6 text-white" />}
          bg="bg-orange-500"
        />
      </div>

      <Card extra="p-5">
          <p className="mb-4 text-lg font-bold text-navy-700 dark:text-white">Recent Inquiries</p>
          <div className="flex flex-col gap-3">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 border-b border-gray-100 pb-3 dark:border-white/10 last:border-0 last:pb-0">
                  <div className="h-9 w-9 animate-pulse rounded-full bg-orange-100 dark:bg-navy-700 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-28 animate-pulse rounded bg-orange-100 dark:bg-navy-700" />
                    <div className="h-3 w-40 animate-pulse rounded bg-gray-100 dark:bg-navy-700" />
                  </div>
                </div>
              ))
            ) : stats?.aRecentInquiries?.length ? (
              stats.aRecentInquiries.map((inq) => (
                <div key={inq._id} className="flex items-center justify-between border-b border-gray-100 pb-3 dark:border-white/10 last:border-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">
                      {inq.sName?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-navy-700 dark:text-white truncate">{inq.sName}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[200px]">{inq.sMessage}</p>
                    </div>
                  </div>
                  <span className={`ml-3 shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                    inq.eStatus === "Pending"
                      ? "bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400"
                      : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                  }`}>
                    {inq.eStatus}
                  </span>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <MdContactMail className="mb-2 h-8 w-8 opacity-40" />
                <p className="text-sm">No inquiries yet</p>
              </div>
            )}
          </div>
      </Card>
    </div>
  );
};

export default Dashboard;
